import { lazy } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import App from "../App";
import { BreadCrumbItem } from "../components/bread-crumb";
import AuthAppLayout from "../layout/auth-layout";
import NonAuthLayout from "../layout/non-auth-layout";
import ChangePassword from "../pages/change-password/change-password";
import IndiaDesignEventPage from "../pages/directory/directory-master/india-design-event";
import OnlineUsersPage from "../pages/jb-library/OnlineUsers/onlineusers";
import Photos from "../pages/jb-library/photos/photos";
import ProductionDrawingLink from "../pages/production-planning/components/production-drawing/production-drawing-link";
import ProductionDrawingPreview from "../pages/production-planning/components/production-drawing/production-drawing-preview";
import DraftWorkOrders from "../pages/production-planning/draft-work-order/draft-work-order";
import CreateUpdateInsuranceAgentPage from "../pages/settings/invoice/insurance-agent/create-update-insurance-agent";
import OfficeCoverageOverview from "../pages/settings/offices/office-coverage-overview";
import RegionWiseOfficeCitiesMapping from "../pages/settings/offices/region-wise-office-cities-mapping";
import PricingVersionsOrderWiseCounts from "../pages/settings/pricing-version/pricing-versions-order-wise-counts";
import DepartmentSupplyPage from "../pages/settings/production-department/department-supply/department-supply";
import ProductionMenuPage from "../pages/settings/production-department/production-menu";
import UserReassignment from "../pages/settings/users/user-reassign";
import UnderMaintenance from "../pages/sign-in/under-maintenance";
import SendForTBS from "../pages/tbs-tracker/tbs-sender/send-for-tbs";
import SentForTBSSteps from "../pages/tbs-tracker/tbs-sender/send-for-tbs-steps";
import TBSStore from "../pages/tbs-tracker/tbs-store/tbs-store";
import VerifyTBSItem from "../pages/tbs-tracker/tbs-store/verify-tbs-items";
import ReturnExtraItems from "../pages/tbs-tracker/tbs-store/return-extra-items";
import ViewTBSItem from "../pages/tbs-tracker/tbs-store/view-tbs-items";
import ViewInvoiceDetails from "../pages/transactions/invoices/invoice-action/view-invoice-details";
import TrustSealPage from "../pages/trust-seal/pages/public-page/trust-seal";
import ViewLedgerDetails from "../pages/view-ledger-details/view-ledger-details";
import { allFeaturedModules } from "../utils/constants";
import { ProductionDepartmentEnumEntity } from "../utils/enum";
import Department from "../pages/settings/production-master/Department";
import MasterTrack from "../pages/settings/production-master/Master-Track";
import WorkCenter from "../pages/settings/production-master/Work-Center";
import WorkStation from "../pages/settings/production-master/Work-Station";
import NamedTrack from "../pages/settings/production-master/Name-Track";
import WorkOrderHoldReason from "../pages/settings/production-master/Work-Order-Hold-Reason";
import SalesHeadHoldReason from "../pages/settings/production-master/Sales-Head-Hold-Reason";
import TBSCategory from "../pages/settings/tbs-category/tbs-category";
import CSMasterTypes from "../pages/settings/cs-master-types/CS-Master-Types";
import CSMaster from "../pages/settings/cs-master/cs-master";
import BOMMapping from "../pages/settings/bom-mapping/bom-mapping";
import BOMUOM from "../pages/settings/bom-uom/bom-uom";
import WorkflowRoutes from "../pages/settings/workflow-routes/workflow-routes";

/*
  Breadcrumb hubs mirroring the sidebar's own navigation groups (see
  components/side-bar.tsx) — reuse one of these on a route's
  `breadcrumbTrail` instead of retyping the label/url. A hub with no `url`
  (e.g. DIRECTORY_HUB) is a pure umbrella category in the sidebar with no
  landing page of its own; it still renders as a plain (non-clickable)
  breadcrumb segment. Adding a new page under one of these sections? Copy
  the matching hub onto the new route's breadcrumbTrail.
*/
const SETTINGS_HUB: BreadCrumbItem = { label: allFeaturedModules.settings, url: '/settings' };
const CAMPAIGN_HUB: BreadCrumbItem = { label: allFeaturedModules.campaign, url: '/campaign' };
const SOCIALS_HUB: BreadCrumbItem = { label: allFeaturedModules.socials };
const PRODUCTION_PLANNING_HUB: BreadCrumbItem = { label: allFeaturedModules.ProductionPlanning };
const DIRECTORY_HUB: BreadCrumbItem = { label: allFeaturedModules.directory };
const JB_LIBRARY_HUB: BreadCrumbItem = { label: allFeaturedModules.JBLibrary };
const TRANSACTIONS_HUB: BreadCrumbItem = { label: allFeaturedModules.transactions };
const WALL_ORDERS_HUB: BreadCrumbItem = { label: allFeaturedModules.wallOrders, url: '/wall-orders' };

/*
  Create/edit solution pages (regular, accessory-only, and glass) are all
  opened FROM manage-wall-order-solution/:orderId for that same order (see
  order-solution-detail.tsx's "ADD ..."/row-edit actions) — so their trail
  must link to THAT order's own Manage Solutions page, not the generic
  Wall Orders hub. A plain array can't carry the current order's id, so
  breadcrumbTrail takes this builder instead and AuthAppLayout runs it
  with the route's own params.
*/
const manageSolutionsTrail = (params: Readonly<Record<string, string | undefined>>): BreadCrumbItem[] => [
  WALL_ORDERS_HUB,
  { label: 'Manage Solutions', url: `/manage-wall-order-solution/${params.orderId}` },
];

const ViewTrustSeal = lazy(() => import("../pages/trust-seal/pages/ViewTrustSeal"));
const AdditionalDealerDiscount = lazy(
  () => import("../pages/settings/offices/additional-dealer-discount/additional-dealer-discount")
);

const SignInPage = lazy(() => import("../pages/sign-in/sign-in"));

const PercentageUpdatePage = lazy(() => import("../pages/settings/percentage-update/percentage-update-view"));
const AddPercentageUpdatePage = lazy(() => import("../pages/settings/percentage-update/add-percentage-update"));
const DashboardPage = lazy(() => import('../pages/dashboard/dashboard'));
const TrafficLightDashboardView = lazy(() => import('../pages/dashboard/traffic-light-dashboard-view'));
const ObservationErrorPage = lazy(() => import('../pages/observation-errors/observation-errors'));
const ViewErrorDetailsPage = lazy(() => import('../pages/observation-errors/view-error-details'));

const CampaignPage = lazy(() => import('../pages/campaign/campaign'));

const CreateCampaignPage = lazy(() => import('../pages/campaign/create-update-campaign'));

const CampaignSummaryPage = lazy(() => import('../pages/campaign/campaign-summary'));

const ViewCampaignSummaryPage = lazy(() => import('../pages/campaign/view-campaign-summary'));

const WhatsAppHistoryPage = lazy(() => import('../pages/campaign/whatsapp-history'));

const EmailHistoryPage = lazy(() => import('../pages/campaign/email-history'));

const PaymentresponsePage = lazy(() => import('../pages/payment-response/payment-response'));

//role
const RoleManagementPage = lazy(
  () => import("../pages/role-management/role-management")
);
const CreateUpdateRoleManagementPage = lazy(
  () => import("../pages/role-management/create-update-role-management")
);

const IDVisitorPage = lazy(
  () => import("../pages/directory/india-design/ID-visitors/ID-visitors")
);

const IDInvitationPage = lazy(
  () => import("../pages/directory/india-design/ID-invitations/ID-invitations")
);

// settings
const DocumentationPage = lazy(
  () => import("../pages/settings/documentation/documentation")
);
const SettingsPage = lazy(() => import("../pages/settings/settings"));
const GeneralSettingPage = lazy(
  () => import("../pages/settings/general-settings/general-settings")
);

const UsersListPage = lazy(() => import("../pages/settings/users/users-list"));
const UserAttachmentPage = lazy(() => import("../pages/settings/users/user-attachments"));

const CreateUpdateUserPage = lazy(
  () => import("../pages/settings/users/create-update-user")
);

const PricingVersionPage = lazy(
  () => import("../pages/settings/pricing-version/pricing-version")
);
const ViewChangeLogsPage = lazy(
  () => import("../pages/settings/view-change-logs/view-change-logs")
);
const CreateNewVersionPage = lazy(
  () => import("../pages/settings/create-new-version/create-new-version")
);

const SystemTypePage = lazy(
  () => import("../pages/settings/system-type/system-types")
);
const SubSystemPage = lazy(
  () => import("../pages/settings/sub-system/sub-system")
);
// const BrandsPage = lazy(() => import('../pages/settings/brands/brands'));
const SystemPage = lazy(() => import("../pages/settings/systems/systems"));
const CreateUpdateSystemPage = lazy(
  () => import("../pages/settings/systems/create-update-systems")
);
const ColorPage = lazy(
  () => import("../pages/settings/frame-colors/frame-colors")
);
const WaltzVariantsPage = lazy(
  () => import("../pages/settings/waltz-variants/waltz-variants")
);
const CreateUpdateWaltzVariantsPage = lazy(
  () => import("../pages/settings/waltz-variants/create-update-waltz-variants")
);
const ModelsPage = lazy(() => import("../pages/settings/models/models"));
const CreateUpdateModelPage = lazy(
  () => import("../pages/settings/models/create-update-models")
);
// const FrameFinishesPage = lazy(() => import('../pages/settings/frame-finishes/frame-finishes'));
const WaltzCategoriesPage = lazy(
  () => import("../pages/settings/waltz-categories/waltz-categories")
);
const WaltzSubCategoriesPage = lazy(
  () => import("../pages/settings/waltz-sub-categories/waltz-sub-categories")
);
const WaltzFinishesPage = lazy(
  () => import("../pages/settings/waltz-finishes/waltz-finishes")
);
const CreateUpdateWaltzFinishesPage = lazy(
  () => import("../pages/settings/waltz-finishes/create-update-waltz-finishes")
);

const CreateUpdateSubCategoriesCataloguePage = lazy(
  () => import("../pages/settings/waltz-sub-categories/create-update-sub-categories-catalogue")
);

const HandlesPage = lazy(() => import("../pages/settings/handles/handles"));
// const HandlesVariantsPage = lazy(() => import('../pages/settings/handles-variants/handles-variants'));
// const CreateUpdateHandlesVariantsPage = lazy(() => import('../pages/settings/handles-variants/create-update-handles-variants'));
const DesignsPage = lazy(() => import("../pages/settings/designs/designs"));
const CreateUpdateDesignsPage = lazy(
  () => import("../pages/settings/designs/create-update-designs")
);
const GridsPage = lazy(() => import("../pages/settings/grids/grids"));
const CreateUpdateGridsPage = lazy(
  () => import("../pages/settings/grids/create-update-grids")
);

const DrawingsPage = lazy(() => import('../pages/settings/drawings/drawings'));
// const CreateUpdateDrawingsPage = lazy(() => import('../pages/settings/drawings/create-update-drawings'));
const LockPage = lazy(() => import("../pages/settings/lock/lock"));

const CountriesStatesCitiesMasterPage = lazy(
  () => import("../pages/settings/countries-states-cities-master/countries-states-cities-master")
);
const OfficesPage = lazy(() => import("../pages/settings/offices/offices"));
const CreateUpdateOfficesPage = lazy(
  () => import("../pages/settings/offices/create-update-offices")
);
const ViewRegionWiseOfficePage = lazy(
  () => import("../pages/settings/offices/view-region-wise-office")
);

const OfficeFiles = lazy(
  () => import("../pages/settings/offices/office-files")
);

const PaymentPage = lazy(() => import("../pages/transactions/payment/payment"));
const PaymentHistoryPage = lazy(
  () => import("../pages/transactions/payment-history/payment-history")
);

// const OrderDuePage = lazy(() => import("../pages/order-due/order-due"));

const SettlementsPage = lazy(
  () => import("../pages/transactions/settlements/settlements")
);
const CreateUpdateArchitectDetailsPage = lazy(
  () => import("../pages/directory/create-update-architect-firm")
);
const DirectoryMasterPage = lazy(
  () => import("../pages/directory/directory-master/directory-master")
);

const LeadsSharedMasterPage = lazy(
  () => import("../pages/directory/leads/leads")
);

const CreateTraderBySocialPage = lazy(
  () => import("../pages/directory/trader-by-social/create-trader-social")
);

const TraderBySocialPage = lazy(
  () => import("../pages/directory/trader-by-social/trader-by-social")
);

const LeadsSharedViewPage = lazy(
  () => import("../pages/directory/leads/leads-shared-view")
);

const ArchitectDetailsPage = lazy(
  () => import("../pages/directory/architect-firm")
);
const DraftTransactionsPage = lazy(
  () => import("../pages/transactions/draft-transactions/draft-transactions")
);
const CreateUpdateUserDirectoryPage = lazy(
  () =>
    import("../pages/directory/directory-master/create-update-directory-user")
);
const CreateUpdateCompanyDirectoryPage = lazy(
  () =>
    import(
      "../pages/directory/directory-master/create-update-directory-company"
    )
);
const RefundPaymentPage = lazy(
  () => import("../pages/transactions/refund-payment/refund-payment")
);
const RefundPaymentHistoryPage = lazy(
  () =>
    import(
      "../pages/transactions/refund-payment-history/refund-payment-history"
    )
);

const InvoicesPage = lazy(
  () =>
    import(
      "../pages/transactions/invoices/invoices"
    )
);

const CreditNotePage = lazy(
  () =>
    import(
      "../pages/transactions/credit-note/credit-note-view"
    )
);

const EWayBillHistoryPage = lazy(
  () =>
    import(
      "../pages/transactions/invoices/invoice-action/eWayBillHistory"
    )
);

const ExtendEWayBillValidityPage = lazy(
  () =>
    import(
      "../pages/transactions/invoices/invoice-action/extendEWayBillValidity"
    )
);

const ManageCreateGlassSolutionsPage = lazy(
  () =>
    import(
      "../pages/wall-orders/manage-solutions/manage-glass-solution-details"
    )
);
const ManageEditGlassSolutionsPage = lazy(
  () =>
    import(
      "../pages/wall-orders/manage-solutions/edit-glass-order-solution-details"
    )
);

const UpdatePartPage = lazy(
  () =>
    import(
      "../pages/transactions/invoices/invoice-action/updatePart"
    )
);

// prime-react-data-table
// const PrimeReactDataTablePage = lazy(() => import('../pages/prime-react-data-tables/prime-react-data-tables'));

// download Module
const DownloadsPage = lazy(() => import("../pages/downloads/downloads"));

//anylatics module
const AnalyticsPage = lazy(() => import("../pages/analytics/analytics"));

const MeetingConfigurationPage = lazy(
  () => import("../pages/jb-connect/meeting-configuration/meeting-configuration")
);

// wall order
const OrdersPage = lazy(() => import("../pages/wall-orders/orders/orders"));
const ManageOrderPage = lazy(
  () => import("../pages/wall-orders/manage-order/manage-order")
);
const ManageSolutionsPage = lazy(
  () => import("../pages/wall-orders/manage-solutions/manage-solutions")
);
const ManageCreateSolutionsPage = lazy(
  () => import("../pages/wall-orders/manage-solutions/manage-solution-details")
);
const ManageEditSolutionsPage = lazy(
  () => import("../pages/wall-orders/manage-solutions/edit-solution-details")
);
const LedgerFormPage = lazy(
  () => import("../pages/wall-orders/ledger-form/ledger-form")
);
// const ProjectDirectoryPage = lazy(
//   () => import("../pages/wall-orders/orders/project-directory")
// );
const CustomerBillingForm = lazy(
  () =>
    import("../pages/wall-orders/customer-billing-form/customer-billing-form")
);
const ViewWaltzOrderPage = lazy(
  () => import("../pages/wall-orders/view-waltz-order/view-waltz-order")
);
const CreateUpdatePanelOnly = lazy(
  () => import("../pages/settings/panel-only/create-update-panel-only")
);

const CreateUpdateAccessoryOnly = lazy(
  () => import("../pages/settings/accessory-only/create-update-accessory-only")
);

// const CreateAccessoryOnlySolutionsOldPage = lazy(
//   () =>
//     import(
//       "../pages/wall-orders/manage-solutions/accessory-only-old/create-accessory-only-solution"
//     )
// );

// const EditAccessoryOnlySolutionsOldPage = lazy(
//   () =>
//     import(
//       "../pages/wall-orders/manage-solutions/accessory-only-old/edit-accessory-only-solution"
//     )
// );

const CreateAccessoryOnlySolutionsPage = lazy(
  () =>
    import(
      "../pages/wall-orders/manage-solutions/create-accessory-only-solution"
    )
);

const EditAccessoryOnlySolutionsPage = lazy(
  () =>
    import(
      "../pages/wall-orders/manage-solutions/edit-accessory-only-solution"
    )
);

// Page not found
const PageNotFoundPage = lazy(
  () => import("../pages/page-not-found/page-not-found")
);
const ForbiddenPage = lazy(() => import("../pages/page-not-found/forbidden"));

const RedFlagPage = lazy(() => import("../pages/red-flag/red-flag"));

const MettingsPage = lazy(
  () => import("../pages/jb-connect/meetings/meetings")
);

const B2BPage = lazy(
  () => import("../pages/directory/B2B/b2b")
);

const ViewMettingsPage = lazy(
  () => import("../pages/jb-connect/meetings/view-meetings")
);

const Tags = lazy(() => import('../pages/jb-library/tags/tags'));

const Albums = lazy(() => import('../pages/jb-library/albums/albums'));

const UserSignInTrackingPage = lazy(
  () => import("../pages/jb-library/user-sign-in-tracking/user-sign-in-tracking")
);

const ExecutionOrderDetailPage = lazy(
  () => import("../pages/production-planning/execution-order-detail/execution-order-detail")
);

const ProductionWorkOrderListDetailPage = lazy(
  () => import("../pages/production-planning/production-work-order-list/production-work-order-list")
);

const EditPendingWorkOrderListDetailPage = lazy(
  () => import("../pages/production-planning/production-work-order-list/edit-pending-work-order")
);

const ViewReadyWorkOrderListDetailPage = lazy(
  () => import("../pages/production-planning/production-work-order-list/view-ready-work-order")
);

const DmBicDataTabDetailPage = lazy(
  () => import("../pages/production-planning/dm-bic-data-tab/DM-BIC-data-tab")
);

const ShopDrawingAwaitingTabDetailPage = lazy(
  () => import("../pages/production-planning/dm-bic-data-tab/shop-drawing-awaiting/shop-drawing-awaiting")
);

const MasterDaDetailPage = lazy(
  () => import("../pages/production-planning/master-da/master-da")
);


const CreateMasterDaDetailPage = lazy(
  () => import("../pages/production-planning/master-da/create-master-da")
);

const ViewMasterDaDetailPage = lazy(
  () => import("../pages/production-planning/master-da/view-master-delivery-advice-details")
);


const ViewGeneratedWorkOrderDetailsPage = lazy(
  () => import("../pages/production-planning/components/view-generated-work-order-details")
)
const ProductionTeamWorkOrder = lazy(() => import("../pages/production-planning/production-team/production-team"));

const SalesPersonWorkOrder = lazy(() => import("../pages/production-planning/sales-person/sales-person-work-order"));

const DispatchManageWorkOrder = lazy(() => import("../pages/production-planning/dispatch-manager/dispatch-manager-work-order"));

const WorkOrderDetailViewPage = lazy(() => import("../pages/production-planning/work-order-list/work-order-details"));
const WorkOrderDetailsListPage = lazy(() => import("../pages/production-planning/work-order-list/work-order-list"));
const WorkOrderTrackerPage = lazy(() => import("../pages/production-planning/work-order-tracker/work-order-tracker"));
const DeliveryAdviceCreatePage = lazy(() => import("../pages/production-planning/components/delivery-advice/v2/delivery-advice-steps"));
const LocationTrackerPage = lazy(() => import("../pages/location-tracker/location-tracker"));
const ProjectTrackerPage = lazy(() => import("../pages/project-tracker/project-tracker"));
const PaintJobListPage = lazy(() => import("../pages/paint-jobs/paint-job-list"));
const RMProjectionPage = lazy(() => import("../pages/production-planning/rm-projection/rm-projection"));
const RMProjectionDetailsPage = lazy(() => import("../pages/production-planning/rm-projection/rm-projection-details"));
const EditDeliveryAdvicePage = lazy(() => import("../pages/production-planning/components/delivery-advice/edit-delivery-advice-list"))

const SolutionComparisonsPages = lazy(() => import("../pages/wall-orders/orders/solution-comparisons"));

const EducationListingAssetsPage = lazy(() => import("../pages/education-module/education-listing-assets"));
const EducationFromAssetsPage = lazy(() => import("../pages/education-module/education-form-assets"));
const PreviewEducationAssetsPage = lazy(() => import("../pages/education-module/preview-education-assets"));
const StateToUserMapping = lazy(() => import("../pages/socials/state-to-user-mapping/state-to-user-mapping"));
const AddUserToState = lazy(() => import("../pages/socials/state-to-user-mapping/add-user-to-state"));
const SocialLeads = lazy(() => import("../pages/socials/leads/social-leads"));
const AddLeads = lazy(() => import("../pages/socials/leads/add-leads"));
const ConvertToOrder = lazy(() => import("../pages/socials/leads/convert-to-order"))

// const DrawingUploadPage = lazy(
//   () => import("../pages/production-planning/execution-order-detail/drawing-upload")
// ); 

const SplitPDFPage = lazy(() => import("../pages/pdf-pin-design/split-pdf-page"));

const PDFAnnotationPage = lazy(() => import("../pages/pdf-pin-design/pdf-annotation"));

const DrawingPage = lazy(() => import("../pages/settings/drawings-set/drawingPage"))

const TransporterListPage = lazy(() => import("../pages/settings/invoice/transporter/transporterList"))

const CreateUpdateTransporterPage = lazy(() => import("../pages/settings/invoice/transporter/create-update-transporter"));

const InvoiceTermsAndConditionsPage = lazy(() => import("../pages/settings/invoice/Invoice-term-condition"));
const InsuranceAgentsPage = lazy(() => import("../pages/settings/invoice/insurance-agent/insurance-agents"));
const HsnCodeGstMappingPage = lazy(() => import("../pages/settings/invoice/hsn-code-gst-mapping"));

const InsurancePolicyPage = lazy(() => import("../pages/settings/invoice/insurance-policy"));

const CreateUpdateInsurancePolicyPage = lazy(() => import("../pages/settings/invoice/transporter/create-update-insurance-policy"));
const ShopDrawingLinkedPage = lazy(() => import("../pages/production-planning/pre-work-order/shop-drawing-linked"));

const QRCodeBlankPage = lazy(() => import("../pages/production-planning/pre-work-order/qa-code-blank-page"));

const ShopDrawingPreviewPage = lazy(() => import("../pages/production-planning/pre-work-order/shop-drawing-preview"));

const ShopDrawingStampApprovedPage = lazy(() => import("../pages/production-planning/pre-work-order/shop-drawing-stamp-approved"));

const VisionPage = lazy(() => import("../pages/user-vision/vision"));
const ViewWorkOrderDetailsPage = lazy(() => import("../pages/production-planning/draft-work-order/view-work-order-details"));

const ErrorProductionTeam = lazy(() => import("../pages/settings/production-department/error-production/error-productionteam"))
function AppRoutes() {
  return (
    <>
      <App />

      <BrowserRouter>
        <Routes>
          <Route path="" element={<NonAuthLayout page={<SignInPage />} />} />

          <Route
            path="/view-ledger-details/:id"
            element={
              <NonAuthLayout page={<ViewLedgerDetails modelOrderId={""} ledgerDetailsCB={() => (null)} />} />
            }
          />

          <Route
            path="dashboard"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.dashboard}
                page={<DashboardPage />}
              />
            }
          />

          <Route
            path="settings/percentage-update"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.percentageUpdate}
                page={<PercentageUpdatePage />}
              />
            }
          />

          <Route
            path="dashboard"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.dashboard}
                page={<DashboardPage />}
              />
            }
          />
          <Route
            path="traffic-light-dashboard/:orderId"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.dashboard}
                page={<TrafficLightDashboardView />}
              />
            }
          />
          <Route
            path="settings/percentage-update"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.percentageUpdate}
                page={<PercentageUpdatePage />}
              />
            }
          />

          <Route
            path="dashboard"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.dashboard}
                page={<DashboardPage />}
              />
            }
          />
          <Route
            path="traffic-light-dashboard/:orderId"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.dashboard}
                page={<TrafficLightDashboardView />}
              />
            }
          />
          <Route
            path="campaign"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.campaign}
                page={<CampaignPage />}
              />
            }
          />

          <Route
            path="campaign/create-campaign/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.campaign}
                breadcrumbTrail={[CAMPAIGN_HUB]}
                breadcrumbLeaf="Create Campaign"
                page={<CreateCampaignPage />}
              />
            }
          />

          <Route
            path="campaign/edit-campaign/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.campaign}
                breadcrumbTrail={[CAMPAIGN_HUB]}
                breadcrumbLeaf="Edit Campaign"
                page={<CreateCampaignPage />}
              />
            }
          />

          <Route
            path="campaign/campaign-summary/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.campaign}
                breadcrumbTrail={[CAMPAIGN_HUB]}
                breadcrumbLeaf="Campaign Summary"
                page={<CampaignSummaryPage />}
              />
            }
          />

          <Route
            path="campaign/view-campaign-summary/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.campaign}
                breadcrumbTrail={[CAMPAIGN_HUB]}
                breadcrumbLeaf="View Campaign Summary"
                page={<ViewCampaignSummaryPage />}
              />
            }
          />

          <Route
            path="whatsapp-history"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.campaign}
                breadcrumbTrail={[CAMPAIGN_HUB]}
                breadcrumbLeaf="WhatsApp History"
                page={<WhatsAppHistoryPage />}
              />
            }
          />

          <Route
            path="email-history"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.campaign}
                breadcrumbTrail={[CAMPAIGN_HUB]}
                breadcrumbLeaf="Email History"
                page={<EmailHistoryPage />}
              />
            }
          />

          <Route
            path="settings/percentage-update"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.percentageUpdate}
                page={<PercentageUpdatePage />}
              />
            }
          />

          <Route
            path="settings/percentage-update/add-percentage-update"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.percentageUpdate}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Percentage Update', url: '/settings/percentage-update' }]}
                breadcrumbLeaf="Add Percentage Update"
                page={<AddPercentageUpdatePage />}
              />
            }
          />

          <Route
            path="role-management"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.roleManagement}
                page={<RoleManagementPage />}
              />
            }
          />

          {/* <Route
            path="role-management/create-update-role"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.dashboard}
                page={<CreateUpdateRoleManagementPage />}
              />
            }
          /> */}

          <Route
            path="role-management/create-update-role-new"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.dashboard}
                page={<CreateUpdateRoleManagementPage />}
              />
            }
          />

          {/* settings */}
          <Route
            path="settings"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.settings}
                page={<SettingsPage />}
              />
            }
          />

          <Route
            path="settings/general-settings"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.generalSettings}
                page={<GeneralSettingPage />}
              />
            }
          />

          <Route
            path="settings/users"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.users}
                page={<UsersListPage />}
              />
            }
          />

          <Route
            path="settings/users/user-attachments/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.users}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Users', url: '/settings/users' }]}
                breadcrumbLeaf="User Attachments"
                page={<UserAttachmentPage />}
              />
            }
          />

          <Route
            path="settings/users/create-update-user"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.users}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Users', url: '/settings/users' }]}
                breadcrumbLeaf="Create User"
                page={<CreateUpdateUserPage />}
              />
            }
          />

          <Route
            path="settings/users/create-update-user/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.users}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Users', url: '/settings/users' }]}
                breadcrumbLeaf="Update User"
                page={<CreateUpdateUserPage />}
              />
            }
          />
          <Route
            path="settings/users/user-reassign/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.users}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Users', url: '/settings/users' }]}
                breadcrumbLeaf="User Reassignment"
                page={<UserReassignment />}
              />
            }
          />
          <Route
            path="settings/pricing-version"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.pricingVersion}
                page={<PricingVersionPage />}
              />
            }
          />

          {/* <Route
            path="settings/pricing-version-order-wise-counts"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.PricingVersionsOrderWiseCounts}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Pricing Version', url: '/settings/pricing-version' }]}
                page={<PricingVersionsOrderWiseCounts />}
              />
            }
          /> */}

          <Route
            path="settings/view-change-logs/:id/"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.pricingVersion}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Pricing Version', url: '/settings/pricing-version' }]}
                breadcrumbLeaf="View Change Logs"
                page={<ViewChangeLogsPage />}
              />
            }
          />

          <Route
            path="settings/create-new-version"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.pricingVersion}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Pricing Version', url: '/settings/pricing-version' }]}
                breadcrumbLeaf={(_, search) => search.get('isEditMode') ? 'Edit Version' : 'Create New Version'}
                page={<CreateNewVersionPage />}
              />
            }
          />

          <Route
            path="settings/system-types"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.systemTypes}
                page={<SystemTypePage />}
              />
            }
          />

          <Route
            path="settings/drawing"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.drawing}
                page={<DrawingPage />}
              />
            }
          />

          <Route
            path="settings/sub-system"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.subSystems}
                page={<SubSystemPage />}
              />
            }
          />

          {/* <Route
                        path="settings/brands"
                        element={<AuthAppLayout
                            featuredPage={allFeaturedModules.brand}
                            page={<BrandsPage />}
                        />}
                    /> */}

          <Route
            path="settings/systems"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.systems}
                page={<SystemPage />}
              />
            }
          />

          <Route
            path="settings/systems/create-update-system"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.systems}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Systems', url: '/settings/systems' }]}
                breadcrumbLeaf={(_, search) => search.get('systemId') ? 'Update System' : 'Create System'}
                page={<CreateUpdateSystemPage />}
              />
            }
          />

          <Route
            path="settings/frame-colors"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.frameColors}
                page={<ColorPage />}
              />
            }
          />

          <Route
            path="settings/waltz-variants"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.waltzVariants}
                page={<WaltzVariantsPage />}
              />
            }
          />

          <Route
            path="settings/waltz-variants/create-update-waltz-variants"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.waltzVariants}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Variants', url: '/settings/waltz-variants' }]}
                breadcrumbLeaf={(_, search) => search.get('waltzVariantId') ? 'Update Variant' : 'Create Variant'}
                page={<CreateUpdateWaltzVariantsPage />}
              />
            }
          />

          <Route
            path="settings/models"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.models}
                page={<ModelsPage />}
              />
            }
          />

          <Route
            path="settings/models/create-update-models"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.models}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Models', url: '/settings/models' }]}
                breadcrumbLeaf={(_, search) => search.get('modelId') ? 'Update Model' : 'Create Model'}
                page={<CreateUpdateModelPage />}
              />
            }
          />

          <Route
            path="settings/waltz-categories"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.waltzCategories}
                page={<WaltzCategoriesPage />}
              />
            }
          />

          <Route
            path="settings/waltz-sub-categories"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.waltzSubCategories}
                page={<WaltzSubCategoriesPage />}
              />
            }
          />

          <Route
            path="settings/waltz-finishes"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.waltzFinishes}
                page={<WaltzFinishesPage />}
              />
            }
          />

          <Route
            path="settings/waltz-finishes/create-update-waltz-finishes"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.waltzFinishes}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Finishes', url: '/settings/waltz-finishes' }]}
                breadcrumbLeaf={(_, search) => search.get('waltzFinishesId') ? 'Update Finish' : 'Create Finish'}
                page={<CreateUpdateWaltzFinishesPage />}
              />
            }
          />

          <Route
            path="settings/waltz-sub-categories/create-update-sub-categories-catalogue/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.waltzFinishes}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Sub Categories', url: '/settings/waltz-sub-categories' }]}
                breadcrumbLeaf="Update Catalogue"
                page={<CreateUpdateSubCategoriesCataloguePage />}
              />
            }
          />

          <Route
            path="settings/handles"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.handles}
                page={<HandlesPage />}
              />
            }
          />
          <Route
            path="settings/create-update-panel-only"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.panelOnly}
                page={<CreateUpdatePanelOnly />}
              />
            }
          />

          <Route
            path="settings/create-update-accessory-only"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.accessoryOnly}
                page={<CreateUpdateAccessoryOnly />}
              />
            }
          />

          {/* <Route
                        path="settings/handles-variants"
                        element={<AuthAppLayout
                            page={<HandlesVariantsPage />}
                        />}
                    /> */}

          {/* <Route
                        path="settings/handles-variants/create-update-handles-variants"
                        element={<AuthAppLayout
                            page={<CreateUpdateHandlesVariantsPage />}
                        />}
                    /> */}

          <Route
            path="settings/designs"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.designs}
                page={<DesignsPage />}
              />
            }
          />

          <Route
            path="settings/designs/create-update-designs"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.designs}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Designs', url: '/settings/designs' }]}
                breadcrumbLeaf={(_, search) => search.get('desginId') ? 'Update Design' : 'Create Design'}
                page={<CreateUpdateDesignsPage />}
              />
            }
          />

          <Route
            path="settings/grids"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.grids}
                page={<GridsPage />}
              />
            }
          />
          {/*
            Settings/Production hub — "settings/production" itself only needs
            "Settings" prepended, which it gets for free from the default rule
            in auth-layout.tsx. Its sub-pages below sit one level deeper than
            that default handles, so each one passes `breadcrumbTrail`
            directly here to get "Home / Settings / Production / <page>".
            Adding a new sub-page under this hub? Copy the breadcrumbTrail
            prop from one of these siblings onto the new route.
          */}
          <Route
            path="settings/production"
            element={<AuthAppLayout
              featuredPage={allFeaturedModules.Production}
              page={<ProductionMenuPage />}
            />}
          />
          <Route
            path="settings/production/department"
            element={<AuthAppLayout
              featuredPage={allFeaturedModules.Department}
              breadcrumbTrail={[{ label: 'Settings', url: '/settings' }, { label: 'Production', url: '/settings/production' }]}
              page={<ErrorProductionTeam />}
            />}
          />
          <Route
            path="settings/production/what-to-supply"
            element={<AuthAppLayout
              featuredPage={allFeaturedModules.WhatToSupply}
              breadcrumbTrail={[{ label: 'Settings', url: '/settings' }, { label: 'Production', url: '/settings/production' }]}
              page={<DepartmentSupplyPage type={ProductionDepartmentEnumEntity.WHAT_TO_SUPPLY} />}
            />}
          />
          <Route
            path="settings/production/why-to-supply"
            element={<AuthAppLayout
              featuredPage={allFeaturedModules.WhyToSupply}
              breadcrumbTrail={[{ label: 'Settings', url: '/settings' }, { label: 'Production', url: '/settings/production' }]}
              page={<DepartmentSupplyPage type={ProductionDepartmentEnumEntity.WHY_TO_SUPPLY} />}
            />}
          />
          <Route
            path="settings/production/action-required"
            element={<AuthAppLayout
              featuredPage={allFeaturedModules.ActionRequired}
              breadcrumbTrail={[{ label: 'Settings', url: '/settings' }, { label: 'Production', url: '/settings/production' }]}
              page={<DepartmentSupplyPage type={ProductionDepartmentEnumEntity.ACTION_REQUIRED} />}
            />}
          />
          <Route
            path="settings/production/master-item"
            element={<AuthAppLayout
              featuredPage={allFeaturedModules.MasterItem}
              breadcrumbTrail={[{ label: 'Settings', url: '/settings' }, { label: 'Production', url: '/settings/production' }]}
              page={<DepartmentSupplyPage type={ProductionDepartmentEnumEntity.MASTER_ITEM} />}
            />}
          />
          <Route
            path="settings/grids/create-update-grids"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.grids}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Grids', url: '/settings/grids' }]}
                breadcrumbLeaf={(_, search) => search.get('gridId') ? 'Update Grid' : 'Create Grid'}
                page={<CreateUpdateGridsPage />}
              />
            }
          />

          <Route
            path="settings/lock"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.locks}
                page={<LockPage />}
              />
            }
          />

          {/* anylatics-routes */}
          <Route
            path="/analytics"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.analytics}
                page={<AnalyticsPage />}
              />
            }
          />

          <Route
            path="/analytics-with-scheduler"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.analytics}
                page={<AnalyticsPage />}
              />
            }
          />

          {/* anylatics-routes */}
          <Route
            path="/observation-errors"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.observationErrors}
                page={<ObservationErrorPage />}
              />
            }
          />

          <Route
            path="/view-error-details/:errorId"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.observationErrors}
                breadcrumbTrail={[{ label: allFeaturedModules.observationErrors, url: '/observation-errors' }]}
                breadcrumbLeaf="View Error Details"
                page={<ViewErrorDetailsPage />}
              />
            }
          />

          {/* download */}
          <Route
            path="/downloads"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.downloads}
                page={<DownloadsPage />}
              />
            }
          />

          {/* <Route path='/prime-react-data-tables' element={<AuthAppLayout page={<PrimeReactDataTablePage />} />} /> */}

          {/* wall-orders */}
          <Route
            path="wall-orders"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.wallOrders}
                page={<OrdersPage />}
              />
            }
          />
          <Route
            path="manage-wall-order"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.wallOrders}
                breadcrumbTrail={[WALL_ORDERS_HUB]}
                breadcrumbLeaf="Manage Order"
                page={<ManageOrderPage />}
              />
            }
          />
          <Route
            path="manage-wall-order-solution/:orderId"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.wallOrders}
                breadcrumbTrail={[WALL_ORDERS_HUB]}
                breadcrumbLeaf="Manage Solutions"
                page={<ManageSolutionsPage />}
              />
            }
          />
          <Route
            path="create-wall-order-solution/:orderId"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.wallOrders}
                breadcrumbTrail={manageSolutionsTrail}
                breadcrumbLeaf="Create Solution"
                page={<ManageCreateSolutionsPage />}
              />
            }
          />
          <Route
            path="edit-wall-order-solution/:orderId/:solutionId"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.wallOrders}
                breadcrumbTrail={manageSolutionsTrail}
                breadcrumbLeaf="Edit Solution"
                page={<ManageEditSolutionsPage />}
              />
            }
          />

          {/* pdf pin design */}
          <Route
            path="/split-drawing-pdf-for-order/:id"
            element={<SplitPDFPage isForOrder={true} />}
          />

          <Route
            path="/split-drawing-pdf/:id/:orderId"
            element={<SplitPDFPage />}
          />

          <Route
            path="/pdf-annotation/:orderId"
            element={<PDFAnnotationPage />}
          />

          <Route
            path="ledger-form/:orderId/"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.wallOrders}
                breadcrumbTrail={[WALL_ORDERS_HUB]}
                breadcrumbLeaf="Ledger Form"
                page={<LedgerFormPage />}
              />
            }
          />

          {/* <Route
            path="project-directory/:orderId/"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.wallOrders}
                page={<ProjectDirectoryPage />}
              />
            }
          /> */}

          <Route
            path="/customer-billing-form"
            element={<NonAuthLayout page={<CustomerBillingForm />} />}
          />

          <Route
            path="view-waltz-order/:orderId/"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.wallOrders}
                breadcrumbTrail={[WALL_ORDERS_HUB]}
                breadcrumbLeaf="View Order"
                page={<ViewWaltzOrderPage />}
              />
            }
          />

          <Route
            path="solution-comparisons/:orderId"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.wallOrders}
                breadcrumbTrail={[WALL_ORDERS_HUB]}
                breadcrumbLeaf="Solution Comparisons"
                page={<SolutionComparisonsPages />}
              />
            }
          />

          <Route
            path="create-accessory-only-solution/:orderId"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.wallOrders}
                breadcrumbTrail={manageSolutionsTrail}
                breadcrumbLeaf="Create Accessory Solution"
                page={<CreateAccessoryOnlySolutionsPage />}
              />
            }
          />

          <Route
            path="edit-accessory-only-solution/:orderId/:solutionId"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.wallOrders}
                breadcrumbTrail={manageSolutionsTrail}
                breadcrumbLeaf="Edit Accessory Solution"
                page={<EditAccessoryOnlySolutionsPage />}
              />
            }
          />

          <Route
            path="/forbidden"
            element={<NonAuthLayout page={<ForbiddenPage />} />}
          />

          <Route path="*" element={<PageNotFoundPage />} />

          <Route
            path="user/change-password"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.changePassword}
                page={<ChangePassword />}
              />
            }
          />

          <Route
            path="settings/countries-states-cities-masters"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.countriesStatesCitiesMaster}
                page={<CountriesStatesCitiesMasterPage />}
              />
            }
          />

          <Route
            path="settings/offices"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.offices}
                page={<OfficesPage />}
              />
            }
          />

          <Route
            path="settings/offices/create-update"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.offices}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Offices', url: '/settings/offices' }]}
                breadcrumbLeaf={(_, search) => search.get('officeId') ? 'Update Office' : 'Create Office'}
                page={<CreateUpdateOfficesPage />}
              />
            }
          />

          <Route
            path="settings/offices/view-region-wise-office"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.offices}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Offices', url: '/settings/offices' }]}
                breadcrumbLeaf="View Region Wise Office"
                page={<ViewRegionWiseOfficePage />}
              />
            }
          />

          <Route
            path="settings/view-office-map"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.offices}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Offices', url: '/settings/offices' }]}
                breadcrumbLeaf="View Office Map"
                page={< OfficeCoverageOverview />}
              />
            }
          />

          <Route
            path="settings/offices/region-wise-office-cities-mapping"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.offices}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Offices', url: '/settings/offices' }]}
                breadcrumbLeaf="Region Wise Office Cities Mapping"
                page={<RegionWiseOfficeCitiesMapping />}
              />
            }
          />

          <Route
            path="settings/offices/office-files"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.offices}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Offices', url: '/settings/offices' }]}
                breadcrumbLeaf="Office Files"
                page={<OfficeFiles />}
              />
            }
          />

          <Route
            path="settings/offices/additional-dealer-discount"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.offices}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Offices', url: '/settings/offices' }]}
                breadcrumbLeaf="Additional Dealer Discount"
                page={<AdditionalDealerDiscount />}
              />
            }
          />

          <Route
            path="create-glass-order-solution/:orderId"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.wallOrders}
                breadcrumbTrail={manageSolutionsTrail}
                breadcrumbLeaf="Create Glass Solution"
                page={<ManageCreateGlassSolutionsPage />}
              />
            }
          />

          <Route
            path="edit-glass-order-solution/:orderId/:solutionId"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.wallOrders}
                breadcrumbTrail={manageSolutionsTrail}
                breadcrumbLeaf="Edit Glass Solution"
                page={<ManageEditGlassSolutionsPage />}
              />
            }
          />


          <Route
            path="/paymentresponse"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.transactions}
                breadcrumbTrail={[TRANSACTIONS_HUB]}
                breadcrumbLeaf="Payment Response"
                page={<PaymentresponsePage />}
              />
            }
          />

          <Route
            path="/under-maintenance"
            element={<NonAuthLayout page={<UnderMaintenance />} />}
          />

          <Route
            path="transactions/payment"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.transactions}
                breadcrumbTrail={[TRANSACTIONS_HUB]}
                breadcrumbLeaf="Make Payment"
                page={<PaymentPage />}
              />
            }
          />

          <Route
            path="transactions/payment-history"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.paymentHistory}
                breadcrumbTrail={[TRANSACTIONS_HUB]}
                page={<PaymentHistoryPage />}
              />
            }
          />

          <Route
            path="transactions/refund-payment-history"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.refundPaymentHistory}
                breadcrumbTrail={[TRANSACTIONS_HUB]}
                page={<RefundPaymentHistoryPage />}
              />
            }
          />

          <Route
            path="transactions/invoices"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.invoices}
                breadcrumbTrail={[TRANSACTIONS_HUB]}
                page={<InvoicesPage />}
              />
            }
          />

          <Route
            path="transactions/credit-note"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.creditNote}
                breadcrumbTrail={[TRANSACTIONS_HUB]}
                page={<CreditNotePage />}
              />
            }
          />

          <Route
            path="transactions/invoices/view-invoice-details/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.invoices}
                breadcrumbTrail={[TRANSACTIONS_HUB, { label: 'Invoices', url: '/transactions/invoices' }]}
                breadcrumbLeaf="View Invoice Details"
                page={<ViewInvoiceDetails />}
              />
            }
          />

          {/* <Route
            path="transactions/e-waybill-history/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.invoices}
                breadcrumbTrail={[TRANSACTIONS_HUB, { label: 'Invoices', url: '/transactions/invoices' }]}
                breadcrumbLeaf="E-Way Bill History"
                page={<EWayBillHistoryPage />}
              />
            }
          /> */}

          {/* <Route
            path="transactions/extend-e-waybill-validity/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.invoices}
                breadcrumbTrail={[TRANSACTIONS_HUB, { label: 'Invoices', url: '/transactions/invoices' }]}
                breadcrumbLeaf="Extend E-Way Bill Validity"
                page={<ExtendEWayBillValidityPage />}
              />
            }
          /> */}

          {/* <Route
            path="transactions/updatePart/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.invoices}
                breadcrumbTrail={[TRANSACTIONS_HUB, { label: 'Invoices', url: '/transactions/invoices' }]}
                breadcrumbLeaf="Update Part"
                page={<UpdatePartPage />}
              />
            }
          /> */}

          <Route
            path="transactions/settlements"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.settlements}
                breadcrumbTrail={[TRANSACTIONS_HUB]}
                page={<SettlementsPage />}
              />
            }
          />

          <Route
            path="transactions/draft-transactions"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.draftTransactions}
                breadcrumbTrail={[TRANSACTIONS_HUB]}
                page={<DraftTransactionsPage />}
              />
            }
          />

          <Route
            path="transactions/refund-payment/:transactionReferenceId"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.refundPayment}
                breadcrumbTrail={[TRANSACTIONS_HUB]}
                page={<RefundPaymentPage />}
              />
            }
          />

          {/* <Route
            path="order-due"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.orderDue}
                page={<OrderDuePage />}
              />
            }
          /> */}

          <Route
            path="create-update-architect-details"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB, { label: 'Architect Details', url: '/architect-details' }]}
                breadcrumbLeaf={(_, search) => search.get('architectId') ? 'Update Architect Details' : 'Create Architect Details'}
                page={<CreateUpdateArchitectDetailsPage />}
              />
            }
          />

          <Route
            path="directory-master"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB]}
                breadcrumbLeaf="Directory Master"
                page={<DirectoryMasterPage />}
              />
            }
          />

          <Route
            path="directory-master/create-update-directory-user"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB, { label: 'Directory Master', url: '/directory-master' }]}
                breadcrumbLeaf={(_, search) => search.get('architectUser_id') ? 'Update Directory User' : 'Create Directory User'}
                page={<CreateUpdateUserDirectoryPage />}
              />
            }
          />

          <Route
            path="/india-design-event"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB]}
                breadcrumbLeaf="India Design"
                page={<IndiaDesignEventPage />}
              />
            }
          />

          <Route
            path="/ID-visitors"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB]}
                breadcrumbLeaf="ID Visitors"
                page={<IDVisitorPage />}
              />
            }
          />

          <Route
            path="/ID-invitations"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB]}
                breadcrumbLeaf="ID Invitations"
                page={<IDInvitationPage />}
              />
            }
          />

          <Route
            path="directory-master/create-update-directory-comapny"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB, { label: 'Directory Master', url: '/directory-master' }]}
                breadcrumbLeaf={(_, search) => search.get('architectFirmId') ? 'Update Directory Company' : 'Create Directory Company'}
                page={<CreateUpdateCompanyDirectoryPage />}
              />
            }
          />

          <Route
            path="directory-master/view-directory-user"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB, { label: 'Directory Master', url: '/directory-master' }]}
                breadcrumbLeaf="View Directory User"
                page={<CreateUpdateUserDirectoryPage />}
              />
            }
          />

          <Route
            path="directory-master/create-update-directory-company"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB, { label: 'Directory Master', url: '/directory-master' }]}
                breadcrumbLeaf={(_, search) => search.get('architectFirmId') ? 'Update Directory Company' : 'Create Directory Company'}
                page={<CreateUpdateCompanyDirectoryPage />}
              />
            }
          />

          <Route
            path="red-flag"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.redFlag}
                page={<RedFlagPage />}
              />
            }
          />

          <Route
            path="architect-details"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB]}
                breadcrumbLeaf="Architect Details"
                page={<ArchitectDetailsPage />}
              />
            }
          />

          <Route
            path="settings/meeting-configuration"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.MeetingConfiguration}
                page={<MeetingConfigurationPage />}
              />
            }
          />

          <Route
            path="meetings"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB]}
                breadcrumbLeaf="Meetings"
                page={<MettingsPage />}
              />
            }
          />

          <Route
            path="b2b"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB]}
                breadcrumbLeaf="B2B"
                page={<B2BPage />}
              />
            }
          />

          <Route
            path="view-meetings"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB, { label: 'Meetings', url: '/meetings' }]}
                breadcrumbLeaf="View Meeting"
                page={<ViewMettingsPage isButtonShow={false} />}
              />
            }
          />
          <Route
            path="view-trader"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB, { label: 'Trader By Social', url: '/trader-by-social' }]}
                breadcrumbLeaf="View Trader"
                page={<ViewMettingsPage isButtonShow={true} />}
              />
            }
          />

          <Route
            path="leads"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB]}
                breadcrumbLeaf="Leads"
                page={<LeadsSharedMasterPage />}
              />
            }
          />

          <Route
            path="leads/view"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB, { label: 'Leads', url: '/leads' }]}
                breadcrumbLeaf="View Lead"
                page={<LeadsSharedViewPage />}
              />
            }
          />

          <Route
            path="shared/view"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB, { label: 'Leads', url: '/leads' }]}
                breadcrumbLeaf="View Lead"
                page={<LeadsSharedViewPage />}
              />
            }
          />

          <Route
            path="trader-by-social"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB]}
                breadcrumbLeaf="Trader By Social"
                page={<TraderBySocialPage />}
              />
            }
          />

          <Route
            path="leads/create-trader-for-social"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB, { label: 'Trader By Social', url: '/trader-by-social' }]}
                breadcrumbLeaf="Create Trader (Social)"
                page={<CreateTraderBySocialPage isForSocial={true} />}
              />
            }
          />
          <Route
            path="meeting/create-trader"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB, { label: 'Meetings', url: '/meetings' }]}
                breadcrumbLeaf="Create Trader"
                page={<CreateTraderBySocialPage isForSocial={false} />}
              />
            }
          />

          <Route
            path="architect-details"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.directory}
                breadcrumbTrail={[DIRECTORY_HUB]}
                breadcrumbLeaf="Architect Details"
                page={<ArchitectDetailsPage />}
              />
            }
          />

          <Route
            path="jb-library/tags"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.Tags}
                breadcrumbTrail={[JB_LIBRARY_HUB]}
                page={<Tags />}
              />
            }
          />

          <Route
            path="jb-library/albums"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.Albums}
                breadcrumbTrail={[JB_LIBRARY_HUB]}
                page={<Albums />}
              />
            }
          />

          <Route
            path="jb-library/photos"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.Photos}
                breadcrumbTrail={[JB_LIBRARY_HUB]}
                page={<Photos />}
              />
            }
          />

          <Route
            path="jb-library/users-tracking"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.UsersTracking}
                breadcrumbTrail={[JB_LIBRARY_HUB]}
                page={<UserSignInTrackingPage />}
              />
            }
          />

          <Route
            path="jb-library/online-users"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.OnlineUsers}
                breadcrumbTrail={[JB_LIBRARY_HUB]}
                page={<OnlineUsersPage />}
              />
            }
          />

          {/* Production Planning routes */}
          <Route
            path="execution-order-detail/:orderId"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.executionOrderDetail}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB]}
                page={<ExecutionOrderDetailPage />}
              />
            }
          />

          <Route
            path="accountant-work-order-list"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.AccountantWOs}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB]}
                page={
                  <ProductionWorkOrderListDetailPage
                    key={"true"}
                    isForAccountWork={true}
                  />
                }
              />
            }
          />

          <Route
            path="draft-work-order-list"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.DraftWOs}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB]}
                page={
                  <DraftWorkOrders />
                }
              />
            }
          />

          <Route
            path="draft-work-order-details/view/:orderReferenceNumber"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.DraftWOs}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB, { label: 'Draft WOs', url: '/draft-work-order-list' }]}
                breadcrumbLeaf="View Work Order Details"
                page={
                  <ViewWorkOrderDetailsPage />
                }
              />
            }
          />

          <Route
            path="production-work-order-list"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.ProductionPlanWOs}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB]}
                page={
                  <ProductionWorkOrderListDetailPage
                    key={"false"}
                    isForAccountWork={false}
                  />
                }
              />
            }
          />

          <Route
            path="edit-pending-work-order/:workOrderId"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.ProductionPlanning}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB, { label: 'Production Plan WOs', url: '/production-work-order-list' }]}
                breadcrumbLeaf="Edit Pending Work Order"
                page={<EditPendingWorkOrderListDetailPage />}
              />
            }
          />

          <Route
            path="production-drawing-link/:id"
            element={<ProductionDrawingLink />
            }
          />
          <Route
            path="production-drawing-preview/:id"
            element={<ProductionDrawingPreview />
            }
          />

          <Route
            path="view-generated-work-order/:orderId/:workOrderId"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.executionOrderDetail}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB]}
                breadcrumbLeaf="View Generated Work Order"
                page={<ViewGeneratedWorkOrderDetailsPage />}
              />
            }
          />

          <Route
            path="production-team-work-order"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.ProductionTeamWOs}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB]}
                page={<ProductionTeamWorkOrder />}
              />
            }
          />

          <Route
            path="sales-person-work-order"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.SalepersonWOs}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB]}
                page={<SalesPersonWorkOrder />}
              />
            }
          />
          <Route
            path="dispatch-manager-work-order"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.DispatchManagerWOs}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB]}
                page={<DispatchManageWorkOrder />}
              />
            }
          />
          <Route
            path="delivery-advice"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.DispatchManagerWOs}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB, { label: 'Dispatch Manager WOs', url: '/dispatch-manager-work-order' }]}
                breadcrumbLeaf="Create Delivery Advice"
                page={<DeliveryAdviceCreatePage />}
              />
            }
          />
          <Route
            path="edit-delivery-advice/:uuid"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.DispatchManagerWOs}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB, { label: 'Dispatch Manager WOs', url: '/dispatch-manager-work-order' }]}
                breadcrumbLeaf="Edit Delivery Advice"
                page={<EditDeliveryAdvicePage />}
              />
            }
          />

          <Route
            path="work-order-details"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.workOrdersList}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB]}
                page={<WorkOrderDetailsListPage />}
              />
            }
          />

          <Route
            path="work-order-tracker"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.WOTracker}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB]}
                breadcrumbLeaf="WO Tracker"
                page={<WorkOrderTrackerPage />}
              />
            }
          />

          <Route
            path="location-tracker"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.LocationTracker}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB]}
                breadcrumbLeaf="Location Tracker"
                page={<LocationTrackerPage />}
              />
            }
          />

          <Route
            path="project-tracker"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.ProjectTracker}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB]}
                breadcrumbLeaf="Project Tracker"
                page={<ProjectTrackerPage />}
              />
            }
          />

          <Route
            path="paint-job-list"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.PaintJob}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB]}
                breadcrumbLeaf="Paint Jobs"
                page={<PaintJobListPage />}
              />
            }
          />

          <Route
            path="rm-projection"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.RMProjection}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB]}
                breadcrumbLeaf="RM Projection"
                page={<RMProjectionPage />}
              />
            }
          />

          <Route
            path="rm-projection/details"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.RMProjection}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB, { label: 'RM Projection', url: '/rm-projection' }]}
                breadcrumbLeaf="Details"
                page={<RMProjectionDetailsPage />}
              />
            }
          />

          <Route
            path="view-work-order/:workOrderId"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.workOrdersList}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB, { label: 'Work Orders List', url: '/work-order-details' }]}
                breadcrumbLeaf="View Work Order"
                page={<WorkOrderDetailViewPage />}
              />
            }
          />
          <Route
            path="view-ready-work-order"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.ProductionPlanning}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB, { label: 'Production Plan WOs', url: '/production-work-order-list' }]}
                breadcrumbLeaf="View Ready Work Order"
                page={<ViewReadyWorkOrderListDetailPage />}
              />
            }
          />
          <Route
            path="project-manager-bic-data"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.ProductionPlanning}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB]}
                breadcrumbLeaf="DM BIC Data"
                page={<DmBicDataTabDetailPage />}
              />
            }
          />
          <Route
            path="project-manager-bic-data/shop-drawing-awaiting/:orderId"
            element={<AuthAppLayout
              featuredPage={allFeaturedModules.ProductionPlanning}
              breadcrumbTrail={[PRODUCTION_PLANNING_HUB, { label: 'DM BIC Data', url: '/project-manager-bic-data' }]}
              breadcrumbLeaf="Shop Drawing Awaiting"
              page={<ShopDrawingAwaitingTabDetailPage />}
            />}
          />
          {/* <Route
            path="jb-library/file-dialog"
            element={<AuthAppLayout
              featuredPage={allFeaturedModules.Tags}
              page={<UploadFilesDialog />}
            />}
          /> */}

          <Route
            path="master-delivery-advice"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.ProductionPlanning}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB]}
                breadcrumbLeaf={allFeaturedModules.masterDa}
                page={<MasterDaDetailPage />}
              />
            }
          />

          <Route
            path="create-master-delivery-advice"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.ProductionPlanning}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB, { label: allFeaturedModules.masterDa, url: '/master-delivery-advice' }]}
                breadcrumbLeaf="Create Master DA"
                page={<CreateMasterDaDetailPage />}
              />
            }
          />

          <Route
            path="view-master-delivery-advice/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.masterDa}
                breadcrumbTrail={[PRODUCTION_PLANNING_HUB, { label: allFeaturedModules.masterDa, url: '/master-delivery-advice' }]}
                breadcrumbLeaf="View Master DA"
                page={<ViewMasterDaDetailPage />}
              />
            }
          />

          <Route
            path="education-assets-module"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.goProAssetsModule}
                page={<EducationListingAssetsPage />}
              />
            }
          />

          <Route
            path="settings/education-module"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.goPro}
                page={<EducationListingAssetsPage />}
              />
            }
          />

          <Route
            path="settings/education-form-module"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.goPro}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Go Pro', url: '/settings/education-module' }]}
                breadcrumbLeaf="Create Asset"
                page={<EducationFromAssetsPage />}
              />
            }
          />

          <Route
            path="settings/update-education-form-module/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.goPro}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Go Pro', url: '/settings/education-module' }]}
                breadcrumbLeaf="Update Asset"
                page={<EducationFromAssetsPage />}
              />
            }
          />

          <Route
            path="settings/preview-education-assets/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.goPro}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Go Pro', url: '/settings/education-module' }]}
                breadcrumbLeaf="Preview Asset"
                page={<PreviewEducationAssetsPage />}
              />
            }
          />

          <Route
            path="preview-education-assets/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.goProAssetsModule}
                breadcrumbTrail={[{ label: allFeaturedModules.goProAssetsModule, url: '/education-assets-module' }]}
                breadcrumbLeaf="Preview Asset"
                page={<PreviewEducationAssetsPage />}
              />
            }
          />

          <Route
            path="settings/documentation"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.documentation}
                page={<DocumentationPage />}
              />
            }
          />

          <Route
            path="project-info/:orderCsRefNumber/:sdId"
            element={<QRCodeBlankPage />}
          />
          <Route
            path="shop-drawing-linked/:orderId/:sdId"
            element={<ShopDrawingLinkedPage />}
          />
          <Route
            path="shop-drawing-preview/:orderId/:sdId"
            element={<ShopDrawingPreviewPage />}
          />

          <Route
            path="shop-drawing-stamp-approved/:orderId"
            element={<ShopDrawingStampApprovedPage />}
          />

          <Route
            path="socials/state-to-user-mapping"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.socials}
                breadcrumbTrail={[SOCIALS_HUB]}
                breadcrumbLeaf={allFeaturedModules.stateToUserMapping}
                page={<StateToUserMapping />}
              />
            }
          />

          <Route
            path="socials/state-to-user-mapping/add-user-to-state"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.socials}
                breadcrumbTrail={[SOCIALS_HUB, { label: allFeaturedModules.stateToUserMapping, url: '/socials/state-to-user-mapping' }]}
                breadcrumbLeaf={(_, search) => search.get('edit') === 'true' ? 'Edit User To State' : 'Add User To State'}
                page={<AddUserToState />}
              />
            }
          />

          <Route
            path="socials/leads"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.socials}
                breadcrumbTrail={[SOCIALS_HUB]}
                breadcrumbLeaf="Social Leads"
                page={<SocialLeads />}
              />
            }
          />
          <Route
            path="socials/leads/add"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.socials}
                breadcrumbTrail={[SOCIALS_HUB, { label: 'Social Leads', url: '/socials/leads' }]}
                breadcrumbLeaf="Add Lead"
                page={<AddLeads />}
              />
            }
          />
          <Route
            path="socials/leads/edit/:leadId"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.socials}
                breadcrumbTrail={[SOCIALS_HUB, { label: 'Social Leads', url: '/socials/leads' }]}
                breadcrumbLeaf="Edit Lead"
                page={<AddLeads />}
              />
            }
          />
          <Route
            path="socials/leads/view/:leadId"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.socials}
                breadcrumbTrail={[SOCIALS_HUB, { label: 'Social Leads', url: '/socials/leads' }]}
                breadcrumbLeaf="View Lead"
                page={<AddLeads />}
              />
            }
          />
          <Route
            path="socials/leads/convert-to-order/:leadId"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.socials}
                breadcrumbTrail={[SOCIALS_HUB, { label: 'Social Leads', url: '/socials/leads' }]}
                breadcrumbLeaf="Convert To Order"
                page={<ConvertToOrder />}
              />
            }
          />


          <Route
            path="settings/transporter"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.transporter}
                page={<TransporterListPage />}
              />
            }
          />

          <Route
            path="settings/create-transporter"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.transporter}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Transporter', url: '/settings/transporter' }]}
                breadcrumbLeaf="Create Transporter"
                page={<CreateUpdateTransporterPage isUpdated={false} />}
              />
            }
          />

          <Route
            path="settings/update-transporter/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.transporter}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Transporter', url: '/settings/transporter' }]}
                breadcrumbLeaf="Update Transporter"
                page={<CreateUpdateTransporterPage isUpdated={true} />}
              />
            }
          />

          <Route
            path="/settings/invoice-terms-and-conditions"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.invoiceTnC}
                page={<InvoiceTermsAndConditionsPage />}
              />
            }
          />

          <Route
            path="/settings/insurance-agents"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.insuranceAgents}
                page={<InsuranceAgentsPage />}
              />
            }
          />

          <Route
            path="settings/create-insurance-agent"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.insuranceAgents}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Insurance Agents', url: '/settings/insurance-agents' }]}
                breadcrumbLeaf="Create Insurance Agent"
                page={<CreateUpdateInsuranceAgentPage isUpdated={false} />}
              />
            }
          />

          <Route
            path="settings/update-insurance-agent/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.insuranceAgents}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Insurance Agents', url: '/settings/insurance-agents' }]}
                breadcrumbLeaf="Update Insurance Agent"
                page={<CreateUpdateInsuranceAgentPage isUpdated={true} />}
              />
            }
          />

          <Route
            path="/settings/hsn-code-gst-mapping"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.HSNCodeNGSTMapping}
                page={<HsnCodeGstMappingPage />}
              />
            }
          />

          <Route
            path="/settings/insurance-policy"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.insurancePolicy}
                page={<InsurancePolicyPage />}
              />
            }
          />

          <Route
            path="settings/create-insurance-policy"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.insurancePolicy}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Insurance Policy', url: '/settings/insurance-policy' }]}
                breadcrumbLeaf="Create Insurance Policy"
                page={<CreateUpdateInsurancePolicyPage isUpdated={false} />}
              />
            }
          />

          <Route
            path="settings/update-insurance-policy/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.insurancePolicy}
                breadcrumbTrail={[SETTINGS_HUB, { label: 'Insurance Policy', url: '/settings/insurance-policy' }]}
                breadcrumbLeaf="Update Insurance Policy"
                page={<CreateUpdateInsurancePolicyPage isUpdated={true} />}
              />
            }
          />

          <Route
            path="/settings/vision-admin"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.Vision}
                page={<VisionPage isAdmin={true} />}
              />
            }
          />

          <Route
            path="vision"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.Vision}
                page={<VisionPage isAdmin={false} />}
              />
            }
          />

          <Route
            path="view-trust-seal"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.TrustSeal}
                page={<ViewTrustSeal />}
              />}
          />

          {/* public page, opened from the trust seal QR code of the order PDF: customers have no login */}
          <Route
            path="trust-seal/:id"
            element={<NonAuthLayout page={<TrustSealPage />} />}
          />

          <Route
            path="/TBS/send-for-tbs"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.SendForTBS}
                page={<SendForTBS />}
              />
            }
          />

          <Route
            path="/TBS/send-for-tbs-step/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.SendForTBS}
                page={<SentForTBSSteps />}
              />
            }
          />
          <Route
            path="/TBS/view-tbs-send-item/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.SendForTBS}
                page={<ViewTBSItem />}
              />
            }
          />
          <Route
            path="/TBS/tbs-store"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.TBSStore}
                page={<TBSStore />}
              />
            }
          />
          <Route
            path="/TBS/verify-tbs-item/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.TBSStore}
                page={<VerifyTBSItem />}
              />
            }
          />
          <Route
            path="/TBS/return-extra-items/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.TBSStore}
                page={<ReturnExtraItems />}
              />
            }
          />
          <Route
            path="/TBS/view-tbs-store-item/:id"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.TBSStore}
                page={<ViewTBSItem />}
              />
            }
          />
          <Route
            path="/settings/department-master"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.DepartmentMaster}
                page={<Department />}
              />
            }
          />

          <Route
            path="/settings/master-track"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.MasterTracker}
                page={<MasterTrack />}
              />
            }
          />

          <Route
            path="/settings/work-center"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.WorkCenter}
                page={<WorkCenter />}
              />
            }
          />

          <Route
            path="/settings/work-station"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.WorkStation}
                page={<WorkStation />}
              />
            }
          />

          <Route
            path="/settings/name-track"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.NameTrack}
                page={<NamedTrack />}
              />
            }
          />

          <Route
            path="/settings/work-order-hold-reason"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.WorkOrderHoldReason}
                page={<WorkOrderHoldReason />}
              />
            }
          />

          <Route
            path="/settings/sales-head-hold-reason"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.SalesHeadHoldReason}
                page={<SalesHeadHoldReason />}
              />
            }
          />

          <Route
            path="/settings/tbs-category"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.TBSCategory}
                page={<TBSCategory />}
              />
            }
          />

          <Route
            path="/settings/cs-master"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.CSMaster}
                page={<CSMaster />}
              />
            }
          />

          <Route
            path="/settings/cs-master-types"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.CSMasterTypes}
                page={<CSMasterTypes />}
              />
            }
          />

          <Route
            path="/settings/bom-mapping"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.BOMMapping}
                breadcrumbLeaf="BOM Mapping"
                page={<BOMMapping />}
              />
            }
          />

          <Route
            path="/settings/uom-bom-mapping"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.BOMMappingUOM}
                breadcrumbLeaf="BOM Mapping UOM"
                page={<BOMUOM />}
              />
            }
          />

          <Route
            path="/settings/workflow-routes"
            element={
              <AuthAppLayout
                featuredPage={allFeaturedModules.WorkflowRoutes}
                page={<WorkflowRoutes />}
              />
            }
          />
        </Routes>
      </BrowserRouter >
    </>
  );
}

export default AppRoutes;
